import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Search, ChevronDown, MoreHorizontal } from 'lucide-react'
import Button from '@/components/ui/Button'
import BusinessSelect from '@/components/ui/BusinessSelect'
import DepartmentSelect from '@/components/ui/DepartmentSelect'
import MemberSelect from '@/components/ui/MemberSelect'
import { designTokens } from '@/utils/design-tokens'
import TaskHierarchyTable, { Business, TaskTreeNode } from './components/TaskHierarchyTable'
import TaskDetailPanel, { type TaskNodeType } from './components/TaskDetailPanel'
import { buildTaskEditInitialData } from './taskEditData'
import TaskTypeFilter, {
  TaskColumnFilter,
  usePersistedTaskColumns,
  usePersistedTaskTypes,
} from './components/TaskTypeFilter'
import TaskGroupCreatePanel from './components/TaskGroupCreatePanel'
import TaskSubgroupCreatePanel from './components/TaskSubgroupCreatePanel'
import TaskCreatePanel from './components/TaskCreatePanel'
import TaskItemCreatePanel from './components/TaskItemCreatePanel'
import TaskAddSelectionModal from './components/TaskAddSelectionModal'
import TaskLinkModal from './components/TaskLinkModal'
import BulkStakeholderModal from './components/BulkStakeholderModal'
import BulkDepartmentModal from './components/BulkDepartmentModal'
import BulkCreateModal from './components/BulkCreateModal'
import JudgmentBasisBulkModal from './components/JudgmentBasisBulkModal'
import ReportDetailPanel from '@/pages/project/ReportManagement/components/ReportDetailPanel'
import { TaskGroupService } from '@/api/generated/services/TaskGroupService'
import { TaskSubgroupService } from '@/api/generated/services/TaskSubgroupService'
import { TaskItemService } from '@/api/generated/services/TaskItemService'
import { TaskMapService } from '@/api/generated/services/TaskMapService'
import { BusinessService } from '@/api/generated/services/BusinessService'
import { useAppSelector } from '@/store/hooks'
import { useCanUseAI } from '@/hooks/useCanUseAI'
import { TaskService, TaskReportService } from '@/api'
import { useAlert } from '@/components/ui/Alert/exports'
import type { ReportTemplateDetailResponse } from '@/api/generated/models/ReportTemplateDetailResponse'
import DateRangePicker from '@/components/ui/DateRangePicker'
import {
  getInitialDateRange,
  formatDateForAPI,
  loadDateRangeFromStorage,
  parseDateFromStorage
} from '@/components/ui/DateRangePicker/utils'
import { getThisWeekRange } from '@/utils/date-parser'
import { usePersistedBusinessFilter } from '@/hooks/usePersistedBusinessFilter'

export default function TaskManagementPage() {
  const location = useLocation()
  const {
    businessId: selectedBusinessId,
    businessName: selectedBusiness,
    setBusiness: saveSelectedBusiness,
  } = usePersistedBusinessFilter()
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('')
  const [selectedDepartmentName, setSelectedDepartmentName] = useState('')
  const [selectedMemberId, setSelectedMemberId] = useState('')
  const [selectedMemberName, setSelectedMemberName] = useState('')
  const { visibleTypes, setVisibleTypes } = usePersistedTaskTypes()
  const { visibleColumns, setVisibleColumns, hiddenColumns } = usePersistedTaskColumns()
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('')
  const [isExpanded, setIsExpanded] = useState(true)
  const [collapsedItems, setCollapsedItems] = useState<Set<string>>(new Set())
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false)
  const [isBulkStakeholderModalOpen, setIsBulkStakeholderModalOpen] = useState(false)
  const [isBulkDepartmentModalOpen, setIsBulkDepartmentModalOpen] = useState(false)
  const [isBulkCreateModalOpen, setIsBulkCreateModalOpen] = useState(false)
  const [isJudgmentBasisBulkModalOpen, setIsJudgmentBasisBulkModalOpen] = useState(false)
  const [taskData, setTaskData] = useState<Business[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [businesses, setBusinesses] = useState<Array<{ id: string; name: string }>>([])
  const [isBusinessesLoading, setIsBusinessesLoading] = useState(false)
  // 상세 패널은 id + type만 받아 자체 fetch. detailRefreshKey를 올리면 패널이 재조회.
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [selectedTaskType, setSelectedTaskType] = useState<TaskNodeType | null>(null)
  const [detailRefreshKey, setDetailRefreshKey] = useState(0)
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [scrollToTaskId, setScrollToTaskId] = useState<string | null>(null)
  const [newlyCreatedTaskId, setNewlyCreatedTaskId] = useState<string | null>(null)
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false)
  const [createBusinessId, setCreateBusinessId] = useState('')
  const [createBusinessName, setCreateBusinessName] = useState('')
  const [isCreateSubgroupPanelOpen, setIsCreateSubgroupPanelOpen] = useState(false)
  const [createTaskGroupId, setCreateTaskGroupId] = useState('')
  const [createTaskGroupName, setCreateTaskGroupName] = useState('')
  const [createSubgroupBusinessName, setCreateSubgroupBusinessName] = useState('')
  // 전략과제 수정을 위한 상태
  const [isEditMode, setIsEditMode] = useState(false)
  const [editTaskGroupId, setEditTaskGroupId] = useState('')
  const [editTaskGroupName, setEditTaskGroupName] = useState('')
  // 실행과제 수정을 위한 상태
  const [isEditSubgroupMode, setIsEditSubgroupMode] = useState(false)
  const [editTaskSubgroupId, setEditTaskSubgroupId] = useState('')
  const [editTaskSubgroupName, setEditTaskSubgroupName] = useState('')
  // 수행업무 생성을 위한 상태
  const [isCreateTaskItemPanelOpen, setIsCreateTaskItemPanelOpen] = useState(false)
  const [createTaskId, setCreateTaskId] = useState('')
  const [createTaskName, setCreateTaskName] = useState('')
  const [createTaskItemSubgroupName, setCreateTaskItemSubgroupName] = useState('')
  const [createTaskItemBusinessName, setCreateTaskItemBusinessName] = useState('')
  // 수행업무 수정을 위한 상태
  const [isEditTaskItemMode, setIsEditTaskItemMode] = useState(false)
  const [editTaskItemId, setEditTaskItemId] = useState('')
  const [editTaskItemData, setEditTaskItemData] = useState<{
    name: string
    startAt: string
    endAt: string
    output: string
  } | undefined>(undefined)
  // 세부실행과제 추가 선택 모달을 위한 상태
  const [isTaskAddSelectionModalOpen, setIsTaskAddSelectionModalOpen] = useState(false)
  const [selectedTaskSubgroupForAdd, setSelectedTaskSubgroupForAdd] = useState('')

  // 세부실행과제 선택 모달을 위한 상태
  const [isTaskSelectModalOpen, setIsTaskSelectModalOpen] = useState(false)
  const [selectedTaskSubgroupId, setSelectedTaskSubgroupId] = useState('')
  const [selectedTaskSubgroupName, setSelectedTaskSubgroupName] = useState('')

  // 세부실행과제 생성을 위한 상태
  const [isCreateTaskPanelOpen, setIsCreateTaskPanelOpen] = useState(false)
  const [createTaskSubgroupId, setCreateTaskSubgroupId] = useState('')
  const [createTaskSubgroupNameForTask, setCreateTaskSubgroupNameForTask] = useState('')
  const [createTaskGroupNameForTask, setCreateTaskGroupNameForTask] = useState('')
  const [createTaskBusinessNameForTask, setCreateTaskBusinessNameForTask] = useState('')
  const [createTaskBusinessId, setCreateTaskBusinessId] = useState('')
  // 세부실행과제 수정을 위한 상태
  const [isEditTaskMode, setIsEditTaskMode] = useState(false)
  const [editTaskId, setEditTaskId] = useState('')
  const [editTaskData, setEditTaskData] = useState<any>(null)

  // Event propagation prevention flag
  const [isActionButtonClicked, setIsActionButtonClicked] = useState(false)

  // 수행업무 데이터 새로고침 중 상태

  // 세부실행과제 데이터 새로고침 중 상태

  // Report panel state
  const [reportPanelState, setReportPanelState] = useState({
    isOpen: false,
    selectedTask: null as TaskTreeNode | null,
    selectedTemplate: null as { key: string; name: string } | null
  })
  const [reportTemplates, setReportTemplates] = useState<ReportTemplateDetailResponse[]>([])
  const [isReportTemplatesLoading, setIsReportTemplatesLoading] = useState(false)
  const [reportTemplatesError, setReportTemplatesError] = useState<string | null>(null)
  // (fetchedReportDetail 제거 — ReportDetailPanel이 자체 fetch)

  // Time period selection state - always default to 'all'
  const [timePeriod, setTimePeriod] = useState<'all' | 'period'>('all')

  // Date range state - initialize as null (lazy initialization on period tab click)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  // Time period toggle handler with period initialization
  const handleTimePeriodChange = (value: 'all' | 'period') => {
    setTimePeriod(value)

    // Initialize period filter when switching to 'period' tab
    if (value === 'period') {
      try {
        // Try to load from localStorage first
        const stored = loadDateRangeFromStorage()
        if (stored.start_date && stored.end_date) {
          const storedStart = parseDateFromStorage(stored.start_date)
          const storedEnd = parseDateFromStorage(stored.end_date)
          if (storedStart && storedEnd) {
            setStartDate(storedStart)
            setEndDate(storedEnd)
            return
          }
        }
      } catch (err) {
        console.log('Failed to load stored period, using this week\'s range')
      }

      // Fall back to this week's Monday-Sunday
      const thisWeek = getThisWeekRange()
      setStartDate(parseDateFromStorage(thisWeek.start))
      setEndDate(parseDateFromStorage(thisWeek.end))
    }
  }

  // State to trigger re-render when localStorage changes
  const [storageUpdateTrigger, setStorageUpdateTrigger] = useState(0)

  // Get display dates from localStorage (for DateRangePicker display)
  const displayDates = useMemo((): [Date | null, Date | null] => {
    try {
      const stored = loadDateRangeFromStorage()
      return [
        parseDateFromStorage(stored.start_date),
        parseDateFromStorage(stored.end_date)
      ]
    } catch {
      const [initialStart, initialEnd] = getInitialDateRange()
      return [initialStart, initialEnd]
    }
  }, [storageUpdateTrigger])

  // Get selected workspace from Redux store
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  
  // Get current organization member from Redux store for role-based permissions
  const orgMember = useAppSelector(state => state.auth.orgMember)
  const hasAdminPrivileges = orgMember?.role === 'owner' || orgMember?.role === 'admin'
  // 판단근거는 AI 분석 프롬프트용이라 AI가 켜진 워크스페이스에서만 다룬다
  const canUseAI = useCanUseAI()

  // Alert hooks for user feedback
  const { showSuccess, showError } = useAlert()

  // 이전 로딩 상태 추적 (데이터 새로고침 후 스크롤 처리용)
  const prevIsLoadingRef = useRef(isLoading)
  // 새로 생성된 항목 ID 추적 (데이터 새로고침 후 스크롤 및 하이라이트 처리용)
  const pendingNewItemIdRef = useRef<string | null>(null)

  // 데이터 로딩 완료 후 스크롤 처리 (선택된 항목 또는 새로 생성된 항목)
  useEffect(() => {
    // 로딩이 완료되었을 때 (true → false)
    if (prevIsLoadingRef.current && !isLoading) {
      // 새로 생성된 항목이 있으면 우선 처리
      if (pendingNewItemIdRef.current) {
        const newItemId = pendingNewItemIdRef.current
        pendingNewItemIdRef.current = null

        // 약간의 딜레이 후 스크롤 및 하이라이트 (그리드 렌더링 완료 대기)
        setTimeout(() => {
          setScrollToTaskId(newItemId)
          setNewlyCreatedTaskId(newItemId)

          // 6초 후 하이라이트 제거
          setTimeout(() => {
            setNewlyCreatedTaskId(null)
          }, 6000)
        }, 100)
      } else if (selectedTaskId) {
        // 새로 생성된 항목이 없으면 선택된 항목으로 스크롤
        setTimeout(() => {
          setScrollToTaskId(selectedTaskId)
        }, 100)
      }
    }
    prevIsLoadingRef.current = isLoading
  }, [isLoading, selectedTaskId])

  // 알림에서 진입 시 해당 항목으로 스크롤 및 하이라이트
  const lastHandledLocationKeyRef = useRef<string | null>(null)
  const notificationScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const notificationHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    const state = location.state as { from?: string; task_id?: string; task_item_id?: string } | null
    if (!state || state.from !== 'notification') return
    if (lastHandledLocationKeyRef.current === location.key) return
    if (isLoading) return

    const targetId = state.task_item_id || state.task_id
    if (!targetId) return

    lastHandledLocationKeyRef.current = location.key

    // 이전 타이머 정리
    if (notificationScrollTimerRef.current) clearTimeout(notificationScrollTimerRef.current)
    if (notificationHighlightTimerRef.current) clearTimeout(notificationHighlightTimerRef.current)
    setNewlyCreatedTaskId(null)

    notificationScrollTimerRef.current = setTimeout(() => {
      setScrollToTaskId(targetId)
      setNewlyCreatedTaskId(targetId)

      notificationHighlightTimerRef.current = setTimeout(() => {
        setNewlyCreatedTaskId(null)
      }, 6000)
    }, 200)

    window.history.replaceState({}, '')
  }, [location.key, location.state, isLoading])

  // Debounce search value for performance
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue)
    }, 300)

    return () => clearTimeout(handler)
  }, [searchValue])

  // Fetch businesses using BusinessService API
  const fetchBusinesses = useCallback(async () => {
    if (!selectedWorkspace?.id) {
      console.warn('No workspace selected for businesses fetch')
      return
    }

    setIsBusinessesLoading(true)

    try {
      const response = await BusinessService.appsBusinessViewsListBusinesses({
        workspaceId: selectedWorkspace.id,
        page: 1,
        perPage: 100
      })

      if (response.data && Array.isArray(response.data)) {
        const businessList = response.data.map(business => ({
          id: business.id || '',
          name: business.name || ''
        }))
        setBusinesses(businessList)
      } else {
        console.warn('No business data in API response or data is not an array')
        setBusinesses([])
      }
    } catch (err) {
      console.error('Failed to fetch businesses:', err)
      setBusinesses([])
    } finally {
      setIsBusinessesLoading(false)
    }
  }, [selectedWorkspace?.id])

  // Fetch businesses when workspace changes
  useEffect(() => {
    if (selectedWorkspace?.id) {
      fetchBusinesses()
    }
  }, [selectedWorkspace?.id, fetchBusinesses])

  // Deep filtering function - returns filtered hierarchy showing only matching items
  // Search targets: task_group.name, task_subgroup.name, task.name, task_item.name only
  const filterTaskHierarchy = useCallback((searchTerm: string, businesses: Business[]): Business[] => {
    if (!searchTerm.trim()) return businesses

    const lowerSearchTerm = searchTerm.toLowerCase()

    return businesses
      .map(business => {
        // Filter task groups (business name is NOT searched)
        const filteredTaskGroups = business.task_groups
          .map(taskGroup => {
            // Task group name matches → return entire task group
            if (taskGroup.name.toLowerCase().includes(lowerSearchTerm)) {
              return taskGroup
            }

            // Filter task subgroups
            const filteredTaskSubgroups = taskGroup.task_subgroups
              .map(taskSubgroup => {
                // Task subgroup name matches → return entire subgroup
                if (taskSubgroup.name.toLowerCase().includes(lowerSearchTerm)) {
                  return taskSubgroup
                }

                // Filter tasks
                const filteredTasks = taskSubgroup.tasks
                  .map(task => {
                    // Task name matches → return entire task
                    if (task.name.toLowerCase().includes(lowerSearchTerm)) {
                      return task
                    }

                    // Filter task items
                    const filteredTaskItems = task.task_items.filter(
                      taskItem => taskItem.name.toLowerCase().includes(lowerSearchTerm)
                    )

                    // Return task if has matching items
                    if (filteredTaskItems.length > 0) {
                      return { ...task, task_items: filteredTaskItems }
                    }

                    return null
                  })
                  .filter((task): task is NonNullable<typeof task> => task !== null)

                // Return subgroup if has matching tasks
                if (filteredTasks.length > 0) {
                  return { ...taskSubgroup, tasks: filteredTasks }
                }

                return null
              })
              .filter((subgroup): subgroup is NonNullable<typeof subgroup> => subgroup !== null)

            // Return task group if has matching subgroups
            if (filteredTaskSubgroups.length > 0) {
              return { ...taskGroup, task_subgroups: filteredTaskSubgroups }
            }

            return null
          })
          .filter((group): group is NonNullable<typeof group> => group !== null)

        // Return business if has matching task groups
        if (filteredTaskGroups.length > 0) {
          return { ...business, task_groups: filteredTaskGroups }
        }

        return null
      })
      .filter((business): business is NonNullable<typeof business> => business !== null)
  }, [])

  // Filter taskData based on selected business, department, member, and search term
  const filteredTaskData = useMemo(() => {
    let filtered = taskData

    // Filter by selected business first
    if (selectedBusiness && selectedBusiness !== '' && selectedBusiness !== '전체') {
      filtered = filtered.filter(business => business.name === selectedBusiness)
    }

    // Filter by department - deep filter through hierarchy
    if (selectedDepartmentId && selectedDepartmentName !== '전체') {
      filtered = filtered
        .map(business => ({
          ...business,
          task_groups: business.task_groups
            .map(taskGroup => ({
              ...taskGroup,
              task_subgroups: taskGroup.task_subgroups
                .map(taskSubgroup => ({
                  ...taskSubgroup,
                  tasks: taskSubgroup.tasks
                    .filter(task => task.department_id === selectedDepartmentId)
                    .map(task => ({
                      ...task,
                      task_items: task.task_items.filter(
                        taskItem => taskItem.department_id === selectedDepartmentId
                      )
                    }))
                }))
                .filter(taskSubgroup => taskSubgroup.tasks.length > 0)
            }))
            .filter(taskGroup => taskGroup.task_subgroups.length > 0)
        }))
        .filter(business => business.task_groups.length > 0)
    }

    // Filter by member (assignee, sub_assignee, manager, reviewer) - OR condition
    if (selectedMemberId && selectedMemberName !== '전체') {
      // Helper function to check if any role matches the selected member
      const matchesMember = (item: any) => {
        return (
          item.assignee?.id === selectedMemberId ||
          item.sub_assignee?.id === selectedMemberId ||
          item.manager?.id === selectedMemberId ||
          item.reviewer?.id === selectedMemberId
        )
      }

      filtered = filtered
        .map(business => ({
          ...business,
          task_groups: business.task_groups
            .map(taskGroup => ({
              ...taskGroup,
              task_subgroups: taskGroup.task_subgroups
                .map(taskSubgroup => ({
                  ...taskSubgroup,
                  tasks: taskSubgroup.tasks
                    .filter(task => matchesMember(task))
                    .map(task => ({
                      ...task,
                      task_items: task.task_items.filter(
                        taskItem => matchesMember(taskItem)
                      )
                    }))
                }))
                .filter(taskSubgroup => taskSubgroup.tasks.length > 0)
            }))
            .filter(taskGroup => taskGroup.task_subgroups.length > 0)
        }))
        .filter(business => business.task_groups.length > 0)
    }

    // Apply deep hierarchical filter for search
    if (debouncedSearchValue.trim()) {
      filtered = filterTaskHierarchy(debouncedSearchValue, filtered)
    }

    return filtered
  }, [taskData, selectedBusiness, selectedDepartmentId, selectedDepartmentName, selectedMemberId, selectedMemberName, debouncedSearchValue, filterTaskHierarchy])

  const handleBusinessSelect = (businessId: string, businessName: string) => {
    saveSelectedBusiness(businessId, businessName)
  }

  const handleDepartmentSelect = (departmentId: string, departmentName: string) => {
    setSelectedDepartmentId(departmentId)
    setSelectedDepartmentName(departmentName)
    // Reset member selection when department changes
    setSelectedMemberId('')
    setSelectedMemberName('')
  }

  const handleMemberSelect = (memberId: string, memberName: string) => {
    setSelectedMemberId(memberId)
    setSelectedMemberName(memberName)
  }

  const handleSearch = () => {
    // Search is handled automatically by filteredTaskData useMemo
    // No need to fetch data again, just trigger re-render
    console.log('Searching for:', searchValue)
  }

  // Handle date range change from DateRangePicker
  const handleDateRangeChange = (newStartDate: Date | null, newEndDate: Date | null) => {
    // Only update if both dates are provided (indicating a complete range selection)
    if (newStartDate && newEndDate) {
      setStartDate(newStartDate)
      setEndDate(newEndDate)
      // Trigger display dates update (localStorage was updated by DateRangePicker)
      setStorageUpdateTrigger(prev => prev + 1)
    }
  }

  const fetchTaskData = useCallback(async (): Promise<Business[]> => {
    if (!selectedWorkspace?.id) {
      console.warn('No workspace selected for task data fetch')
      return []
    }

    setIsLoading(true)
    setError(null)

    try {
      // Format dates for API if they exist
      const apiParams: {
        workspaceId: string
        page: number
        perPage: number
        startDate?: string
        endDate?: string
      } = {
        workspaceId: selectedWorkspace.id,
        page: 1,
        perPage: 100
      }

      // Add date parameters only if timePeriod is 'period' and both dates are set
      if (timePeriod === 'period' && startDate && endDate) {
        apiParams.startDate = formatDateForAPI(startDate)
        apiParams.endDate = formatDateForAPI(endDate)
      }

      const response = await TaskService.appsTaskViewsListAllTaskInWorkspace(apiParams)

      if (response.data && Array.isArray(response.data)) {
        const data = response.data as Business[]
        setTaskData(data)
        return data
      } else {
        console.warn('No data in API response or data is not an array')
        setTaskData([])
        return []
      }
    } catch (err) {
      console.error('Failed to fetch task data:', err)
      setError('업무 데이터를 불러오는데 실패했습니다.')
      return []
    } finally {
      setIsLoading(false)
    }
  }, [selectedWorkspace?.id, timePeriod, startDate, endDate])

  // Fetch data when workspace changes
  useEffect(() => {
    if (selectedWorkspace?.id) {
      fetchTaskData()
    }
  }, [selectedWorkspace?.id, fetchTaskData])

  const handleExpand = () => {
    const newIsExpanded = !isExpanded
    setIsExpanded(newIsExpanded)

    if (!newIsExpanded) {
      // 전체 접기 - 모든 부모 항목을 collapsedItems에 추가
      const parentItems = new Set<string>()

      taskData.forEach((business) => {
        parentItems.add(business.name)
        business.task_groups.forEach(taskGroup => {
          parentItems.add([business.name, taskGroup.name].join('|'))
          taskGroup.task_subgroups.forEach(taskSubgroup => {
            parentItems.add([business.name, taskGroup.name, taskSubgroup.name].join('|'))
            taskSubgroup.tasks.forEach(task => {
              if (task.task_items.length > 0) {
                parentItems.add([business.name, taskGroup.name, taskSubgroup.name, task.name].join('|'))
              }
            })
          })
        })
      })

      setCollapsedItems(parentItems)
    } else {
      // 전체 펼치기 - collapsedItems 비우기
      setCollapsedItems(new Set())
    }
  }

  const handleTaskRowClick = (task: TaskTreeNode) => {
    // Don't open panel if an action button was clicked
    if (isActionButtonClicked) return

    setSelectedTaskId(task.id)
    setSelectedTaskType(task.type)
    setIsPanelOpen(true)
  }

  const handleSetActionButtonClicked = (clicked: boolean) => {
    setIsActionButtonClicked(clicked)
  }

  // Report panel handlers
  const handleReportClick = async (taskItem: TaskTreeNode) => {
    // Set action button click flag to prevent row click (TaskManagement pattern)
    setIsActionButtonClicked(true)
    setTimeout(() => setIsActionButtonClicked(false), 100)

    // Close any open detail panels to prevent double panels
    setIsPanelOpen(false)
    setSelectedTaskId(null)
    setSelectedTaskType(null)

    // Open report panel
    setReportPanelState({
      isOpen: true,
      selectedTask: taskItem,
      selectedTemplate: null
    })

    // Fetch report templates with business_id from the selected task
    if (selectedWorkspace?.id) {
      const businessId = taskItem.originalData?.business_id || taskItem.business_id
      await fetchReportTemplates(selectedWorkspace.id, businessId)
    }
  }

  const handleReportDetailPanelClose = () => {
    setReportPanelState({
      isOpen: false,
      selectedTask: null,
      selectedTemplate: null,
    })
  }

  // 보고서 상세의 "세부실행과제 수정" — 목록에서 세부실행과제를 수정할 때와 같은 수정 패널을 연다.
  // 보고서 패널은 수정 패널보다 뒤에 렌더링돼 같은 z-index 에서 위를 덮으므로 먼저 닫는다.
  const handleReportTaskEdit = () => {
    const task = reportPanelState.selectedTask
    if (!task || task.type !== 'task') return
    handleReportDetailPanelClose()
    handleEditTaskGroup(task)
  }

  const handleReportTypeChange = (reportKey: string, reportName: string) => {
    // 패널이 reportTemplateKey prop 변경을 감지해 자체 refetch함
    setReportPanelState(prev => ({
      ...prev,
      selectedTemplate: { key: reportKey, name: reportName },
    }))
  }

  // (convertTaskTreeNodeToReportResponse 제거 — ReportDetailPanel이 자체 fetch로 데이터 구성)

  // Fetch report templates
  const fetchReportTemplates = async (workspaceId: string, businessId?: string) => {
    setIsReportTemplatesLoading(true)
    setReportTemplatesError(null)

    try {
      const response = await TaskReportService.appsTaskViewsGetReportTemplatesByWorkspace({
        workspaceId,
        businessId: businessId || undefined
      })
      setReportTemplates(response || [])
    } catch (error) {
      console.error('Failed to fetch report templates:', error)
      setReportTemplatesError('보고서 템플릿을 불러오는데 실패했습니다.')
    } finally {
      setIsReportTemplatesLoading(false)
    }
  }

  const handlePanelClose = () => {
    setIsPanelOpen(false)
    setSelectedTaskId(null)
    setSelectedTaskType(null)
  }

  // 목록에서 항목 위치로 스크롤
  const handleScrollToTask = (taskId: string) => {
    setScrollToTaskId(taskId)
  }

  // 스크롤 완료 후 상태 초기화
  const handleScrollComplete = () => {
    setScrollToTaskId(null)
  }

  // 전략과제 수정 핸들러
  const handleEditTaskGroup = (task: TaskTreeNode) => {
    if (task.type === 'task_group') {
      // 사업 정보 찾기
      const business = taskData.find(b => 
        b.task_groups.some(tg => tg.id === task.id)
      )
      
      if (business) {
        setCreateBusinessId(business.id)
        setCreateBusinessName(business.name)
        setEditTaskGroupId(task.id)
        setEditTaskGroupName(task.name)
        setIsEditMode(true)
        setIsCreatePanelOpen(true)
        setIsPanelOpen(false) // 상세 패널 닫기
      }
    } else if (task.type === 'task_subgroup') {
      // 실행과제 수정 처리
      let taskGroup = null
      let business = null
      
      // 전략과제와 사업 정보 찾기
      for (const b of taskData) {
        for (const tg of b.task_groups) {
          if (tg.task_subgroups.some(ts => ts.id === task.id)) {
            taskGroup = tg
            business = b
            break
          }
        }
        if (taskGroup) break
      }
      
      if (business && taskGroup) {
        setCreateTaskGroupId(taskGroup.id)
        setCreateTaskGroupName(taskGroup.name)
        setCreateSubgroupBusinessName(business.name)
        setEditTaskSubgroupId(task.id)
        setEditTaskSubgroupName(task.name)
        setIsEditSubgroupMode(true)
        setIsCreateSubgroupPanelOpen(true)
        setIsPanelOpen(false) // 상세 패널 닫기
      }
    } else if (task.type === 'task_item') {
      // 수행업무 수정 처리
      let parentTask = null
      let taskSubgroup = null
      let business = null

      // 상위 계층 정보 찾기
      for (const b of taskData) {
        for (const tg of b.task_groups) {
          for (const ts of tg.task_subgroups) {
            for (const t of ts.tasks) {
              if (t.task_items.some(ti => ti.id === task.id)) {
                parentTask = t
                taskSubgroup = ts
                business = b
                break
              }
            }
            if (parentTask) break
          }
          if (parentTask) break
        }
        if (parentTask) break
      }

      if (parentTask && taskSubgroup && business) {
        // 수행업무의 원래 데이터 가져오기
        const taskItemData = task.originalData as any

        setCreateTaskId(parentTask.id)
        setCreateTaskName(parentTask.name)
        setCreateTaskItemSubgroupName(taskSubgroup.name)
        setCreateTaskItemBusinessName(business.name)
        setEditTaskItemId(task.id)
        setEditTaskItemData({
          name: task.name,
          startAt: taskItemData?.start_at || '',
          endAt: taskItemData?.end_at || '',
          output: taskItemData?.output || ''
        })
        setIsEditTaskItemMode(true)
        setIsCreateTaskItemPanelOpen(true)
        // 수행업무 수정 시에는 상세 패널을 열린 상태로 유지
        // setIsPanelOpen(false) 제거: 상세 패널을 닫지 않음
      }
    } else if (task.type === 'task') {
      // 세부실행과제 수정 처리
      let taskSubgroup = null
      let taskGroup = null
      let business = null
      
      // 상위 계층 정보 찾기
      for (const b of taskData) {
        for (const tg of b.task_groups) {
          for (const ts of tg.task_subgroups) {
            if (ts.tasks.some(t => t.id === task.id)) {
              taskSubgroup = ts
              taskGroup = tg
              business = b
              break
            }
          }
          if (taskSubgroup) break
        }
        if (taskSubgroup) break
      }
      
      if (taskSubgroup && taskGroup && business) {
        setCreateTaskSubgroupId(taskSubgroup.id)
        setCreateTaskSubgroupNameForTask(taskSubgroup.name)
        setCreateTaskGroupNameForTask(taskGroup.name)
        setCreateTaskBusinessNameForTask(business.name)
        setCreateTaskBusinessId(business.id)
        setEditTaskId(task.id)

        const initialData = buildTaskEditInitialData(task)

        setEditTaskData(initialData)
        setIsEditTaskMode(true)
        setIsCreateTaskPanelOpen(true)
        // 세부실행과제 수정 시에는 상세 패널을 열린 상태로 유지
        // setIsPanelOpen(false) 제거: 상세 패널을 닫지 않음
      }
    }
  }

  // 전략과제/실행과제/수행업무 삭제 핸들러
  const handleDeleteTaskGroup = async (task: TaskTreeNode, memo?: string) => {
    if (task.type === 'task_group') {
      try {
        await TaskGroupService.appsTaskViewsDeleteTaskGroup({
          groupId: task.id,
          requestBody: { memo }
        })

        // 삭제 성공 후 데이터 새로고침
        await fetchTaskData()
      } catch (error) {
        console.error('Failed to delete task group:', error)
        throw error // 상위로 에러 전파
      }
    } else if (task.type === 'task_subgroup') {
      try {
        await TaskSubgroupService.appsTaskViewsDeleteTaskSubGroup({
          subGroupId: task.id,
          requestBody: { memo }
        })

        // 삭제 성공 후 데이터 새로고침
        await fetchTaskData()
      } catch (error) {
        console.error('Failed to delete task subgroup:', error)
        throw error // 상위로 에러 전파
      }
    } else if (task.type === 'task_item') {
      try {
        await TaskItemService.appsTaskViewsDeleteTaskItem({
          itemId: task.id,
          requestBody: { memo }
        })

        // 삭제 성공 후 데이터 새로고침
        await fetchTaskData()
      } catch (error) {
        console.error('Failed to delete task item:', error)
        throw error // 상위로 에러 전파
      }
    }
  }

  // 세부실행과제 추가 모달 열기 핸들러
  const handleCreateTask = (taskSubgroupId: string) => {
    setSelectedTaskSubgroupForAdd(taskSubgroupId)
    setIsTaskAddSelectionModalOpen(true)
  }

  // 세부실행과제 생성 선택 핸들러 (모달에서 "세부실행과제 생성" 선택 시)
  const handleSelectCreateNewTask = () => {
    const taskSubgroupId = selectedTaskSubgroupForAdd

    // 실행과제에서 상위 계층 정보 찾기
    let taskSubgroup = null
    let taskGroup = null
    let business = null

    for (const b of taskData) {
      for (const tg of b.task_groups) {
        for (const ts of tg.task_subgroups) {
          if (ts.id === taskSubgroupId) {
            taskSubgroup = ts
            taskGroup = tg
            business = b
            break
          }
        }
        if (taskSubgroup) break
      }
      if (taskSubgroup) break
    }

    if (taskSubgroup && taskGroup && business) {
      setCreateTaskSubgroupId(taskSubgroupId)
      setCreateTaskSubgroupNameForTask(taskSubgroup.name)
      setCreateTaskGroupNameForTask(taskGroup.name)
      setCreateTaskBusinessNameForTask(business.name)
      setCreateTaskBusinessId(business.id)
      setIsCreateTaskPanelOpen(true)
    }

    // 모달 닫기
    setIsTaskAddSelectionModalOpen(false)
    setSelectedTaskSubgroupForAdd('')
  }

  // 세부실행과제 연결 선택 핸들러 (모달에서 "세부실행과제 연결" 선택 시)
  const handleSelectLinkTask = () => {
    const taskSubgroupId = selectedTaskSubgroupForAdd

    // 실행과제 정보 찾기
    let taskSubgroup = null
    let taskGroup = null
    let business = null

    for (const b of taskData) {
      for (const tg of b.task_groups) {
        for (const ts of tg.task_subgroups) {
          if (ts.id === taskSubgroupId) {
            taskSubgroup = ts
            taskGroup = tg
            business = b
            break
          }
        }
        if (taskSubgroup) break
      }
      if (taskSubgroup) break
    }

    if (taskSubgroup && taskGroup && business) {
      // 계층 정보와 함께 TaskSelectModal 열기
      setSelectedTaskSubgroupId(taskSubgroupId)
      setSelectedTaskSubgroupName(taskSubgroup.name)
      setIsTaskSelectModalOpen(true)
    }

    // TaskAddSelectionModal 닫기
    setIsTaskAddSelectionModalOpen(false)
    setSelectedTaskSubgroupForAdd('')
  }

  // 세부실행과제 추가 모달 닫기 핸들러
  const handleTaskAddSelectionModalClose = () => {
    setIsTaskAddSelectionModalOpen(false)
    setSelectedTaskSubgroupForAdd('')
  }

  // TaskSelectModal에서 세부실행과제 선택 핸들러
  const handleTaskSelect = async (selectedTask: { id: string; name: string }, overwriteName?: string) => {
    if (!selectedTaskSubgroupId) {
      console.error('No task subgroup selected')
      return
    }

    try {
      // TaskMap 생성 API 호출
      await TaskMapService.appsTaskViewsCreateTaskMap({
        requestBody: {
          task_sub_group_id: selectedTaskSubgroupId,
          task_id: selectedTask.id,
          overwrite_name: overwriteName || undefined
        }
      })

      // 성공 토스트 표시
      showSuccess(`세부실행과제 "${selectedTask.name}"이(가) 연결되었습니다.`)

      // 모달 닫기
      setIsTaskSelectModalOpen(false)
      setSelectedTaskSubgroupId('')
      setSelectedTaskSubgroupName('')

      // 데이터 새로고침
      await fetchTaskData()
    } catch (error) {
      console.error('Failed to link task:', error)
      showError('세부실행과제 연결에 실패했습니다.')
    }
  }

  // TaskSelectModal 닫기 핸들러
  const handleTaskSelectModalClose = () => {
    setIsTaskSelectModalOpen(false)
    setSelectedTaskSubgroupId('')
    setSelectedTaskSubgroupName('')
  }

  // 수행업무 생성 핸들러
  const handleCreateTaskItem = (taskId: string) => {
    // 세부실행과제에서 상위 계층 정보 찾기
    let task = null
    let taskSubgroup = null
    let business = null
    
    for (const b of taskData) {
      for (const tg of b.task_groups) {
        for (const ts of tg.task_subgroups) {
          for (const t of ts.tasks) {
            if (t.id === taskId) {
              task = t
              taskSubgroup = ts
              business = b
              break
            }
          }
          if (task) break
        }
        if (task) break
      }
      if (task) break
    }
    
    if (task && taskSubgroup && business) {
      setCreateTaskId(taskId)
      setCreateTaskName(task.name)
      setCreateTaskItemSubgroupName(taskSubgroup.name)
      setCreateTaskItemBusinessName(business.name)
      setIsCreateTaskItemPanelOpen(true)
    }
  }

  const handleCreateTaskGroup = (businessId: string) => {
    const business = taskData.find(b => b.id === businessId)
    if (business) {
      setCreateBusinessId(businessId)
      setCreateBusinessName(business.name)
      setIsCreatePanelOpen(true)
    }
  }

  const handleCreatePanelClose = () => {
    setIsCreatePanelOpen(false)
    setCreateBusinessId('')
    setCreateBusinessName('')
    // 수정 모드 관련 상태 초기화
    setIsEditMode(false)
    setEditTaskGroupId('')
    setEditTaskGroupName('')
  }

  const handleCreateSuccess = (createdId?: string) => {
    // 새로 생성된 항목 ID 저장 (데이터 새로고침 후 스크롤 및 하이라이트용)
    if (createdId) {
      pendingNewItemIdRef.current = createdId
    }
    // Refresh data after successful creation
    fetchTaskData()
  }

  const handleCreateTaskSubgroup = (taskGroupId: string) => {
    // Find the task group and its business context
    for (const business of taskData) {
      const taskGroup = business.task_groups.find(tg => tg.id === taskGroupId)
      if (taskGroup) {
        setCreateTaskGroupId(taskGroupId)
        setCreateTaskGroupName(taskGroup.name)
        setCreateSubgroupBusinessName(business.name)
        setIsCreateSubgroupPanelOpen(true)
        break
      }
    }
  }

  const handleCreateSubgroupPanelClose = () => {
    setIsCreateSubgroupPanelOpen(false)
    setCreateTaskGroupId('')
    setCreateTaskGroupName('')
    setCreateSubgroupBusinessName('')
    // 실행과제 수정 모드 관련 상태 초기화
    setIsEditSubgroupMode(false)
    setEditTaskSubgroupId('')
    setEditTaskSubgroupName('')
  }

  const handleCreateSubgroupSuccess = (createdId?: string) => {
    // 새로 생성된 항목 ID 저장 (데이터 새로고침 후 스크롤 및 하이라이트용)
    if (createdId) {
      pendingNewItemIdRef.current = createdId
    }
    // Refresh data after successful creation
    fetchTaskData()
  }

  const handleCreateTaskItemPanelClose = () => {
    setIsCreateTaskItemPanelOpen(false)
    setCreateTaskId('')
    setCreateTaskName('')
    setCreateTaskItemSubgroupName('')
    setCreateTaskItemBusinessName('')
    // 수행업무 수정 모드 관련 상태 초기화
    setIsEditTaskItemMode(false)
    setEditTaskItemId('')
    setEditTaskItemData(undefined)
  }

  const handleCreateTaskPanelClose = () => {
    setIsCreateTaskPanelOpen(false)
    setCreateTaskSubgroupId('')
    setCreateTaskSubgroupNameForTask('')
    setCreateTaskGroupNameForTask('')
    setCreateTaskBusinessNameForTask('')
    // 세부실행과제 수정 모드 관련 상태 초기화
    setIsEditTaskMode(false)
    setEditTaskId('')
    setEditTaskData(null)
  }

  const handleCreateTaskSuccess = (createdId?: string) => {
    // 새로 생성된 항목 ID 저장 (데이터 새로고침 후 스크롤 및 하이라이트용)
    if (createdId) {
      pendingNewItemIdRef.current = createdId
    }
    // 세부실행과제 생성 성공 후 데이터 새로고침
    fetchTaskData()
  }

  const handleEditTaskSuccess = async () => {
    // 세부실행과제 수정 성공 후: 상세 패널은 열린 상태 유지, 데이터만 새로고침
    try {
      const updatedTaskId = editTaskId

      // 1. 전체 데이터 새로고침 (목록 데이터 fetch)
      await fetchTaskData()

      // 2. 패널이 자체 fetch로 최신 데이터를 다시 가져오도록 refreshKey 증가
      if (updatedTaskId) {
        setDetailRefreshKey(k => k + 1)
      }
    } catch (err) {
      console.error('Failed to refresh task data:', err)
    }
  }

  const handleCreateTaskItemSuccess = (createdId?: string) => {
    // 새로 생성된 항목 ID 저장 (데이터 새로고침 후 스크롤 및 하이라이트용)
    if (createdId) {
      pendingNewItemIdRef.current = createdId
    }
    // 수행업무 생성 성공 후 데이터 새로고침
    fetchTaskData()
  }

  const handleEditTaskItemSuccess = async () => {
    // 수행업무 수정 성공 후: 상세 패널은 열린 상태 유지, 데이터만 새로고침
    try {
      const updatedTaskItemId = editTaskItemId

      // 1. 전체 데이터 새로고침 (목록 데이터 fetch)
      await fetchTaskData()

      // 2. 패널이 자체 fetch로 최신 데이터를 다시 가져오도록 refreshKey 증가
      if (updatedTaskItemId) {
        setDetailRefreshKey(k => k + 1)
      }
    } catch (err) {
      console.error('Failed to refresh task data:', err)
    }
  }

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Filter & Action Bar - Single Row */}
      <div
        className="py-2 px-4 flex-shrink-0 flex items-center gap-2 flex-wrap justify-end rounded-lg"
        style={{
          backgroundColor: designTokens.colors.gray[0]
        }}
      >
        {/* Date Range Selection - Conditional (shown before toggle) */}
        {timePeriod === 'period' && (
          <div className="w-[250px] flex-shrink-0">
            <DateRangePicker
              startDate={displayDates[0]}
              endDate={displayDates[1]}
              onDateRangeChange={handleDateRangeChange}
              timePeriod="period"
              onTimePeriodChange={() => {}}
              triggerMode="icon"
              showDateText={true}
              workspaceId={selectedWorkspace?.id}
            />
          </div>
        )}

        {/* Time Period Toggle */}
        <div className="inline-flex rounded-md shadow-sm h-10 flex-shrink-0" role="group">
          <button
            type="button"
            onClick={() => handleTimePeriodChange('all')}
            className="px-4 font-medium border rounded-l-md transition-colors h-10 whitespace-nowrap"
            style={{
              backgroundColor: timePeriod === 'all' ? designTokens.colors.primary.DEFAULT : designTokens.colors.white,
              color: timePeriod === 'all' ? designTokens.colors.white : designTokens.colors.gray[6],
              borderColor: designTokens.colors.gray[2],
              fontFamily: designTokens.typography.fontFamily.sans[0]
            }}
          >
            전체
          </button>
          <button
            type="button"
            onClick={() => handleTimePeriodChange('period')}
            className="px-4 font-medium border rounded-r-md transition-colors h-10 whitespace-nowrap"
            style={{
              backgroundColor: timePeriod === 'period' ? designTokens.colors.primary.DEFAULT : designTokens.colors.white,
              color: timePeriod === 'period' ? designTokens.colors.white : designTokens.colors.gray[6],
              borderColor: designTokens.colors.gray[2],
              borderLeft: 'none',
              fontFamily: designTokens.typography.fontFamily.sans[0]
            }}
          >
            기간
          </button>
        </div>

        {/* Business Selection */}
        <div className="w-[200px] flex-shrink-0">
          <BusinessSelect
            selectedBusinessId={selectedBusinessId}
            selectedBusinessName={selectedBusiness || '[전체] 사업 선택'}
            onSelect={handleBusinessSelect}
            businesses={businesses}
            showAllOption={true}
            placeholder="[전체] 사업 선택"
          />
        </div>

        {/* Department Selection */}
        <div className="w-[200px] flex-shrink-0">
          <DepartmentSelect
            workspaceId={selectedWorkspace?.id || ''}
            selectedDepartmentId={selectedDepartmentId}
            selectedDepartmentName={selectedDepartmentName || '[전체] 부서 선택'}
            onSelect={handleDepartmentSelect}
            placeholder="[전체] 부서 선택"
            showAllOption={true}
          />
        </div>

        {/* Member Selection */}
        <div className="w-[200px] flex-shrink-0">
          <MemberSelect
            workspaceId={selectedWorkspace?.id || ''}
            selectedMemberId={selectedMemberId}
            selectedMemberName={selectedMemberName || '[전체] 담당자 선택'}
            onSelect={handleMemberSelect}
            placeholder="[전체] 담당자 선택"
            label="담당자"
            showAllOption={true}
          />
        </div>

        {/* Type Selection — 표에 보일 유형 (여러 개 선택) */}
        <div className="w-[180px] flex-shrink-0">
          <TaskTypeFilter visibleTypes={visibleTypes} onChange={setVisibleTypes} />
        </div>

        {/* Column Selection — 표에 보일 컬럼 (여러 개 선택) */}
        <div className="w-[180px] flex-shrink-0">
          <TaskColumnFilter visibleColumns={visibleColumns} onChange={setVisibleColumns} />
        </div>

        {/* Search Input */}
        <div className="relative w-[280px] flex-shrink-0">
          <div
            className="flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded transition-all duration-150"
            style={{
              backgroundColor: designTokens.colors.white,
              borderColor: designTokens.colors.gray[1]
            }}
          >
            <Search
              className="w-5 h-5"
              style={{ color: designTokens.colors.gray[3] }}
            />
            <input
              type="text"
              placeholder="명칭으로 검색해주세요."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="flex-1 bg-transparent outline-none min-w-0"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-medium'][0],
                lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
                color: searchValue ? designTokens.colors.gray[6] : designTokens.colors.gray[3]
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
            />
          </div>
        </div>

        {/* Search Button */}
        <Button
          color="dark"
          size="medium"
          onClick={handleSearch}
          className="h-10 px-[10px] flex-shrink-0 whitespace-nowrap"
        >
          검색
        </Button>

        {/* Expand Button */}
        <Button
          color="dark"
          size="medium"
          onClick={handleExpand}
          className="h-10 px-[10px] flex items-center gap-1 flex-shrink-0 whitespace-nowrap"
        >
          <ChevronDown
            className={`w-[18px] h-[18px] transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            style={{ color: designTokens.colors.gray[0] }}
          />
          {isExpanded ? '접기' : '펼침'}
        </Button>

        {/* More Options Button */}
        <div className="relative flex-shrink-0">
          <Button
            color="dark"
            size="medium"
            onClick={() => setIsMoreMenuOpen(prev => !prev)}
            className="h-10 px-[10px] flex items-center gap-1 whitespace-nowrap"
          >
            <MoreHorizontal
              className="w-[18px] h-[18px]"
              style={{ color: designTokens.colors.gray[0] }}
            />
            더보기
          </Button>

          {isMoreMenuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setIsMoreMenuOpen(false)} />
              <div
                className="absolute right-0 top-full mt-1 w-48 bg-white border rounded shadow-lg z-50"
                style={{ borderColor: designTokens.colors.gray[1] }}
              >
                <button
                  type="button"
                  className="w-full px-4 py-2.5 text-left transition-colors"
                  style={{
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: hasAdminPrivileges ? designTokens.colors.gray[6] : designTokens.colors.gray[3],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    cursor: hasAdminPrivileges ? 'pointer' : 'default',
                  }}
                  disabled={!hasAdminPrivileges}
                  onClick={() => {
                    setIsMoreMenuOpen(false)
                    setIsBulkStakeholderModalOpen(true)
                  }}
                  onMouseEnter={(e) => { if (hasAdminPrivileges) e.currentTarget.style.backgroundColor = designTokens.colors.gray[0] }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
                >
                  담당자 일괄 수정
                </button>
                <button
                  type="button"
                  className="w-full px-4 py-2.5 text-left transition-colors"
                  style={{
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: hasAdminPrivileges ? designTokens.colors.gray[6] : designTokens.colors.gray[3],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    cursor: hasAdminPrivileges ? 'pointer' : 'default',
                  }}
                  disabled={!hasAdminPrivileges}
                  onClick={() => {
                    setIsMoreMenuOpen(false)
                    setIsBulkDepartmentModalOpen(true)
                  }}
                  onMouseEnter={(e) => { if (hasAdminPrivileges) e.currentTarget.style.backgroundColor = designTokens.colors.gray[0] }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
                >
                  부서 일괄 수정
                </button>
                <button
                  type="button"
                  className="w-full px-4 py-2.5 text-left transition-colors"
                  style={{
                    fontSize: designTokens.typography.fontSize['body-medium'][0],
                    color: hasAdminPrivileges ? designTokens.colors.gray[6] : designTokens.colors.gray[3],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    cursor: hasAdminPrivileges ? 'pointer' : 'default',
                  }}
                  disabled={!hasAdminPrivileges}
                  onClick={() => {
                    setIsMoreMenuOpen(false)
                    setIsBulkCreateModalOpen(true)
                  }}
                  onMouseEnter={(e) => { if (hasAdminPrivileges) e.currentTarget.style.backgroundColor = designTokens.colors.gray[0] }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
                >
                  일괄 생성
                </button>
                {canUseAI && (
                  <button
                    type="button"
                    className="w-full px-4 py-2.5 text-left transition-colors"
                    style={{
                      fontSize: designTokens.typography.fontSize['body-medium'][0],
                      color: hasAdminPrivileges ? designTokens.colors.gray[6] : designTokens.colors.gray[3],
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      cursor: hasAdminPrivileges ? 'pointer' : 'default',
                    }}
                    disabled={!hasAdminPrivileges}
                    onClick={() => {
                      setIsMoreMenuOpen(false)
                      setIsJudgmentBasisBulkModalOpen(true)
                    }}
                    onMouseEnter={(e) => { if (hasAdminPrivileges) e.currentTarget.style.backgroundColor = designTokens.colors.gray[0] }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
                  >
                    판단근거 일괄 저장
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 mt-6">
        <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: designTokens.colors.gray[1] }}>
          {/* Loading State */}
          {isLoading && (
            <div className="p-12 text-center">
              <div style={{ 
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-large'][0],
                color: designTokens.colors.gray[4]
              }}>
                업무 데이터를 불러오는 중...
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="p-12 text-center">
              <div style={{ 
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-large'][0],
                color: designTokens.colors.error.DEFAULT,
                marginBottom: '16px'
              }}>
                {error}
              </div>
              <Button
                color="dark"
                size="medium"
                onClick={fetchTaskData}
              >
                다시 시도
              </Button>
            </div>
          )}

          {/* Task Hierarchy Table - API 로딩 중이 아닐 때만 표시 */}
          {!isLoading && !error && (
            <TaskHierarchyTable
              data={filteredTaskData}
              isExpanded={isExpanded}
              isLoading={isLoading}
              onRowClick={handleTaskRowClick}
              onCreateTaskGroup={handleCreateTaskGroup}
              onCreateTaskSubgroup={handleCreateTaskSubgroup}
              onCreateTask={handleCreateTask}
              onCreateTaskItem={handleCreateTaskItem}
              onSetActionButtonClicked={handleSetActionButtonClicked}
              onReportClick={handleReportClick}
              orgMember={orgMember}
              onDataUpdate={setTaskData}
              collapsedItems={collapsedItems}
              onCollapsedItemsChange={setCollapsedItems}
              selectedTaskId={selectedTaskId ?? undefined}
              scrollToTaskId={scrollToTaskId}
              onScrollComplete={handleScrollComplete}
              newlyCreatedTaskId={newlyCreatedTaskId}
              visibleTypes={visibleTypes}
              hiddenColumns={hiddenColumns}
            />
          )}
        </div>
      </div>

      {/* Task Detail Panel — id + type만 전달, 패널이 자체 fetch */}
      <TaskDetailPanel
        isOpen={isPanelOpen}
        onClose={handlePanelClose}
        taskId={selectedTaskId}
        taskType={selectedTaskType}
        refreshKey={detailRefreshKey}
        onEdit={handleEditTaskGroup}
        onDelete={handleDeleteTaskGroup}
        onSuccess={fetchTaskData}
        onScrollToTask={handleScrollToTask}
        showDeleteButton={true}
      />

      {/* Task Group Create Panel */}
      <TaskGroupCreatePanel
        isOpen={isCreatePanelOpen}
        onClose={handleCreatePanelClose}
        businessId={createBusinessId}
        businessName={createBusinessName}
        onSuccess={handleCreateSuccess}
        editMode={isEditMode}
        taskGroupId={editTaskGroupId}
        initialTaskGroupName={editTaskGroupName}
      />

      {/* Task Subgroup Create Panel */}
      <TaskSubgroupCreatePanel
        isOpen={isCreateSubgroupPanelOpen}
        onClose={handleCreateSubgroupPanelClose}
        taskGroupId={createTaskGroupId}
        taskGroupName={createTaskGroupName}
        businessName={createSubgroupBusinessName}
        onSuccess={handleCreateSubgroupSuccess}
        editMode={isEditSubgroupMode}
        taskSubgroupId={editTaskSubgroupId}
        initialTaskSubgroupName={editTaskSubgroupName}
      />

      {/* Task Item Create Panel */}
      <TaskItemCreatePanel
        isOpen={isCreateTaskItemPanelOpen}
        onClose={handleCreateTaskItemPanelClose}
        taskId={createTaskId}
        taskName={createTaskName}
        taskSubgroupName={createTaskItemSubgroupName}
        businessName={createTaskItemBusinessName}
        onSuccess={isEditTaskItemMode ? handleEditTaskItemSuccess : handleCreateTaskItemSuccess}
        editMode={isEditTaskItemMode}
        taskItemId={editTaskItemId}
        initialTaskItemData={editTaskItemData}
      />

      {/* Task Add Selection Modal */}
      <TaskAddSelectionModal
        isOpen={isTaskAddSelectionModalOpen}
        onClose={handleTaskAddSelectionModalClose}
        onSelectLinkTask={handleSelectLinkTask}
        onSelectCreateNew={handleSelectCreateNewTask}
      />

      {/* Task Link Modal */}
      <TaskLinkModal
        isOpen={isTaskSelectModalOpen}
        onClose={handleTaskSelectModalClose}
        onSelect={handleTaskSelect}
        parentCategoryName={selectedTaskSubgroupName}
      />

      {/* Bulk Stakeholder Modal */}
      <BulkStakeholderModal
        isOpen={isBulkStakeholderModalOpen}
        onClose={() => setIsBulkStakeholderModalOpen(false)}
        onSuccess={fetchTaskData}
      />

      {/* Bulk Department Modal */}
      <BulkDepartmentModal
        isOpen={isBulkDepartmentModalOpen}
        onClose={() => setIsBulkDepartmentModalOpen(false)}
        onSuccess={fetchTaskData}
      />

      {/* Bulk Create Modal */}
      <BulkCreateModal
        isOpen={isBulkCreateModalOpen}
        onClose={() => setIsBulkCreateModalOpen(false)}
        onSuccess={fetchTaskData}
      />

      {/* 판단근거 일괄 저장 (엑셀 템플릿 → 업로드) */}
      <JudgmentBasisBulkModal
        isOpen={isJudgmentBasisBulkModalOpen}
        onClose={() => setIsJudgmentBasisBulkModalOpen(false)}
        onSuccess={fetchTaskData}
      />

      {/* Task Create Panel */}
      <TaskCreatePanel
        isOpen={isCreateTaskPanelOpen}
        onClose={handleCreateTaskPanelClose}
        taskSubgroupId={createTaskSubgroupId}
        taskSubgroupName={createTaskSubgroupNameForTask}
        taskGroupName={createTaskGroupNameForTask}
        businessName={createTaskBusinessNameForTask}
        businessId={createTaskBusinessId}
        onSuccess={isEditTaskMode ? handleEditTaskSuccess : handleCreateTaskSuccess}
        editMode={isEditTaskMode}
        taskId={editTaskId}
        initialTaskData={editTaskData}
      />

      {/* Report Detail Panel — id/templateKey만 전달, 패널이 자체 fetch */}
      <ReportDetailPanel
        isOpen={reportPanelState.isOpen}
        onClose={handleReportDetailPanelClose}
        taskId={reportPanelState.selectedTask?.id ?? null}
        reportTemplateKey={reportPanelState.selectedTemplate?.key ?? null}
        reportTemplates={reportTemplates}
        isReportTemplatesLoading={isReportTemplatesLoading}
        reportTemplatesError={reportTemplatesError}
        workspaceId={selectedWorkspace?.id || ''}
        onReportTypeChange={handleReportTypeChange}
        onEditTask={handleReportTaskEdit}
      />

    </div>
  )
}